// src/pages/OwnerOnboarding.tsx
//
// Public Owner Onboarding wizard, mounted at /owner-onboarding/:slug.
// Steps: 0 Landing -> 1 OTP -> 2 Property Setup -> 3 Owner Form -> 4 Identity Documents
//        -> 5 Availability -> 6 Review & Sign -> 7 Confirmation.
// Steps 2-6 are session-guarded (all calls send the X-Owner-Session header).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Loader2, ArrowRight, ArrowLeft, MapPin, Mail, ShieldCheck, CheckCircle,
    KeyRound, FileText, Download, ExternalLink, PenLine, AlertTriangle, RefreshCw,
    UploadCloud, Trash2, BadgeCheck, IdCard, CalendarDays,
} from 'lucide-react';

import {
    getPublicInfo, requestOtp, verifyOtp, getOwnerData,
    savePropertySetup, saveOwnerForm, saveAvailability, signOnboarding, getFinalDocument,
    getDocumentPreview, requestSignOtp, uploadIdentityDocument, deleteIdentityDocument,
} from '../services/ownerOnboardingService';
import { saveOwnerSession, getOwnerSession, clearOwnerSession } from '../utils/ownerSession';
import {
    buildEmptyAnchors, anchorsFromPropertySetup, buildPropertySetupPayload,
} from '../config/propertySetupSchemaConfig';
import {
    AvailabilityData, IdentityDocument, OnboardingDocumentLink, OnboardingOwnerData,
    OnboardingPublicInfo, OwnerForm, PropertySetupAnchors, SignatureMethod, SignRequest,
    AMENDMENT_DOC_LABELS, AmendmentDocKey,
} from '../types';
import PropertySetupForm from '../components/onboarding/PropertySetupForm';
import OwnerFormFields from '../components/onboarding/OwnerFormFields';
import AvailabilityForm, { buildDefaultAvailability, normalizeAvailability } from '../components/onboarding/AvailabilityForm';
import SignatureModal from '../components/onboarding/SignatureModal';
import PdfCanvasViewer from '../components/onboarding/PdfCanvasViewer';

const EMPTY_OWNER_FORM: OwnerForm = {
    ownerFullName: '', dateOfBirth: '', contactPrimary: '', contactSecondary: '',
    emailAddress: '', propertyAddress: '', mailingAddress: '', executionType: 'Individual',
    companyName: '', companyType: '', companyAddress: '', ssn: '', ein: '',
    bankAccountType: 'Checking', bankAccountNumber: '', bankRoutingNumber: '',
    bankName: '', bankAddress: '',
};

type Step = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

const WIZARD_STEPS = [
    { key: 2, label: 'Property Setup' },
    { key: 3, label: 'Owner Form' },
    { key: 4, label: 'Identity Documents' },
    { key: 5, label: 'Availability' },
    { key: 6, label: 'Review & Sign' },
];

// The three documents shown as tabs in the Review & Sign step.
const REVIEW_DOCS = [
    { key: 'property-setup', label: 'Property Setup' },
    { key: 'owner-form', label: 'Owner Form' },
    { key: 'cohosting', label: 'Co-hosting Agreement' },
] as const;
type ReviewDoc = typeof REVIEW_DOCS[number]['key'];

const emptyDocRecord = <T,>(value: T): Record<ReviewDoc, T> => ({
    'property-setup': value,
    'owner-form': value,
    cohosting: value,
});

// Determines the first step the owner still needs to complete.
function firstIncompleteStep(d: OnboardingOwnerData): Step {
    if (d.signedAt) return 7;
    if (!d.propertySetupCompleted) return 2;
    if (!d.ownerFormCompleted) return 3;
    if (!d.identityDocuments || d.identityDocuments.length === 0) return 4;
    if (!d.availabilityData) return 5;
    return 6;
}

function getErrorMessage(err: unknown, fallback: string): string {
    const anyErr = err as { response?: { status?: number; data?: { message?: string } } };
    const status = anyErr?.response?.status;
    if (status === 429) return 'Too many attempts. Please wait a moment and try again.';
    if (status === 401) return 'Your session or code is invalid or has expired. Please try again.';
    return anyErr?.response?.data?.message || fallback;
}

const OwnerOnboarding: React.FC = () => {
    const { slug = '' } = useParams<{ slug: string }>();

    const [step, setStep] = useState<Step>(0);
    const [publicInfo, setPublicInfo] = useState<OnboardingPublicInfo | null>(null);
    const [sessionToken, setSessionToken] = useState<string | null>(null);
    const [ownerData, setOwnerData] = useState<OnboardingOwnerData | null>(null);

    const [propertyAnchors, setPropertyAnchors] = useState<PropertySetupAnchors>(buildEmptyAnchors());
    const [ownerForm, setOwnerForm] = useState<OwnerForm>(EMPTY_OWNER_FORM);
    const [availability, setAvailability] = useState<AvailabilityData>(buildDefaultAvailability());

    // Identity documents (step 4)
    const [identityDocuments, setIdentityDocuments] = useState<IdentityDocument[]>([]);
    const [docUploading, setDocUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // OTP state
    const [otpRequested, setOtpRequested] = useState(false);
    const [otpSending, setOtpSending] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [maskedEmail, setMaskedEmail] = useState('');

    // Sign state (DRAW_OR_UPLOAD)
    const [signatureData, setSignatureData] = useState<string | null>(null);
    const [signatureMethod, setSignatureMethod] = useState<SignatureMethod | null>(null);
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [consent, setConsent] = useState(false);

    // Sign state (OTP)
    const [signOtpRequested, setSignOtpRequested] = useState(false);
    const [signOtpSending, setSignOtpSending] = useState(false);
    const [signOtpCode, setSignOtpCode] = useState('');
    const [signMaskedEmail, setSignMaskedEmail] = useState('');

    // Review & Sign (step 5): tabbed per-document preview
    const [activeDoc, setActiveDoc] = useState<ReviewDoc>('property-setup');
    const [visitedTabs, setVisitedTabs] = useState<Set<ReviewDoc>>(() => new Set());
    const [reviewed, setReviewed] = useState(false);
    const [docUrls, setDocUrls] = useState<Record<ReviewDoc, string | null>>(() => emptyDocRecord<string | null>(null));
    const [docLoading, setDocLoading] = useState<Record<ReviewDoc, boolean>>(() => emptyDocRecord(false));
    const [docErrors, setDocErrors] = useState<Record<ReviewDoc, string | null>>(() => emptyDocRecord<string | null>(null));
    // Tracks docs already fetched or in-flight (guards double fetches); mirrors object URLs for cleanup.
    const requestedDocsRef = useRef<Set<ReviewDoc>>(new Set());
    const docObjectUrlsRef = useRef<string[]>([]);

    // Confirmation
    const [finalPdfUrl, setFinalPdfUrl] = useState<string | null>(null);
    const [documents, setDocuments] = useState<OnboardingDocumentLink[]>([]);
    const [certVerifyUrl, setCertVerifyUrl] = useState<string | null>(null);
    const [certificateUrl, setCertificateUrl] = useState<string | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Load owner data into local editable state.
    const applyOwnerData = useCallback((d: OnboardingOwnerData) => {
        setOwnerData(d);
        setPropertyAnchors(anchorsFromPropertySetup(d.propertySetup));
        setOwnerForm({
            ...EMPTY_OWNER_FORM,
            ...(d.ownerForm || {}),
            // sensible defaults from the record when the form is still empty
            ownerFullName: d.ownerForm?.ownerFullName || `${d.ownerFirstName || ''} ${d.ownerLastName || ''}`.trim(),
            propertyAddress: d.ownerForm?.propertyAddress || d.propertyAddress || '',
        });
        setIdentityDocuments(d.identityDocuments || []);
        try {
            const parsed = d.availabilityData ? (JSON.parse(d.availabilityData) as AvailabilityData) : null;
            setAvailability(normalizeAvailability(parsed));
        } catch {
            setAvailability(buildDefaultAvailability());
        }
        setFinalPdfUrl(d.finalPdfUrl || null);
        setDocuments(d.documents || []);
        setCertVerifyUrl(d.certVerifyUrl || null);
        setCertificateUrl(d.certificateUrl || null);
    }, []);

    // Initial load: public info + resume an existing session if present.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const info = await getPublicInfo(slug);
                if (cancelled) return;
                setPublicInfo(info);
                setMaskedEmail(info.maskedEmail);

                const existing = getOwnerSession(slug);
                if (existing) {
                    try {
                        const d = await getOwnerData(slug, existing);
                        if (cancelled) return;
                        setSessionToken(existing);
                        applyOwnerData(d);
                        setStep(firstIncompleteStep(d));
                    } catch {
                        clearOwnerSession(slug);
                        if (!cancelled) setStep(0);
                    }
                }
            } catch (err) {
                if (!cancelled) setError(getErrorMessage(err, 'This onboarding link is invalid or no longer available.'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [slug, applyOwnerData]);

    // On the confirmation screen, poll owner data until the 3 signed documents and the
    // certified Certificate of Authenticity are available.
    useEffect(() => {
        if (step !== 7 || !sessionToken) return;
        if (documents.length > 0 && certificateUrl) return;
        const tick = async () => {
            try {
                const d = await getOwnerData(slug, sessionToken);
                applyOwnerData(d);
            } catch {
                /* keep polling */
            }
        };
        tick();
        pollRef.current = setInterval(tick, 5000);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [step, documents.length, certificateUrl, sessionToken, slug, applyOwnerData]);

    // Lazily fetch a single document's unsigned preview the first time its tab is opened.
    // The created object URL is cached per doc and tracked for revocation on cleanup.
    const loadDoc = useCallback(async (doc: ReviewDoc) => {
        if (!sessionToken) return;
        if (requestedDocsRef.current.has(doc)) return; // already loaded or in-flight
        requestedDocsRef.current.add(doc);
        setDocLoading((p) => ({ ...p, [doc]: true }));
        setDocErrors((p) => ({ ...p, [doc]: null }));
        try {
            const blob = await getDocumentPreview(slug, sessionToken, doc);
            const url = URL.createObjectURL(blob);
            docObjectUrlsRef.current.push(url);
            setDocUrls((p) => ({ ...p, [doc]: url }));
        } catch (err) {
            requestedDocsRef.current.delete(doc); // allow a retry
            setDocErrors((p) => ({ ...p, [doc]: getErrorMessage(err, 'We could not load this document.') }));
        } finally {
            setDocLoading((p) => ({ ...p, [doc]: false }));
        }
    }, [slug, sessionToken]);

    // Select a tab: mark it visited and lazy-load its preview.
    const selectDoc = useCallback((doc: ReviewDoc) => {
        setActiveDoc(doc);
        setVisitedTabs((prev) => (prev.has(doc) ? prev : new Set(prev).add(doc)));
        void loadDoc(doc);
    }, [loadDoc]);

    // On entering the Review & Sign step, open the first tab (counts as visited + loads it).
    // On leaving the step / unmount, revoke every created object URL and reset the tab state.
    useEffect(() => {
        if (step !== 6 || !sessionToken) return;
        selectDoc('property-setup');
        return () => {
            docObjectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
            docObjectUrlsRef.current = [];
            requestedDocsRef.current = new Set();
            setDocUrls(emptyDocRecord<string | null>(null));
            setDocLoading(emptyDocRecord(false));
            setDocErrors(emptyDocRecord<string | null>(null));
            setVisitedTabs(new Set());
            setActiveDoc('property-setup');
            setReviewed(false);
        };
    }, [step, sessionToken, selectDoc]);

    const refreshOwnerData = useCallback(async (): Promise<OnboardingOwnerData | null> => {
        if (!sessionToken) return null;
        const d = await getOwnerData(slug, sessionToken);
        applyOwnerData(d);
        return d;
    }, [slug, sessionToken, applyOwnerData]);

    // --- OTP handlers ---
    const handleRequestOtp = async () => {
        setOtpSending(true);
        setError(null);
        setNotice(null);
        try {
            const res = await requestOtp(slug);
            setMaskedEmail(res.maskedEmail);
            setOtpRequested(true);
            setNotice(`We sent a 6-digit code to ${res.maskedEmail}.`);
        } catch (err) {
            setError(getErrorMessage(err, 'Could not send the code. Please try again.'));
        } finally {
            setOtpSending(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (otpCode.trim().length < 4) {
            setError('Please enter the code from your email.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const res = await verifyOtp(slug, otpCode.trim());
            saveOwnerSession(slug, res.sessionToken, res.sessionExpiresAt);
            setSessionToken(res.sessionToken);
            const d = await getOwnerData(slug, res.sessionToken);
            applyOwnerData(d);
            setStep(firstIncompleteStep(d));
            setNotice(null);
        } catch (err) {
            setError(getErrorMessage(err, 'Invalid code. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    // --- Step save handlers ---
    const handleSavePropertySetup = async (advance: boolean) => {
        if (!sessionToken) return;
        setSaving(true);
        setError(null);
        try {
            await savePropertySetup(slug, sessionToken, buildPropertySetupPayload(propertyAnchors));
            await refreshOwnerData();
            if (advance) setStep(3);
            else setNotice('Property setup saved.');
        } catch (err) {
            setError(getErrorMessage(err, 'Could not save the property setup.'));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveOwnerForm = async (advance: boolean) => {
        if (!sessionToken) return;
        setSaving(true);
        setError(null);
        try {
            await saveOwnerForm(slug, sessionToken, ownerForm);
            await refreshOwnerData();
            if (advance) setStep(4);
            else setNotice('Owner form saved.');
        } catch (err) {
            setError(getErrorMessage(err, 'Could not save the owner form.'));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAvailability = async (advance: boolean) => {
        if (!sessionToken) return;
        setSaving(true);
        setError(null);
        try {
            await saveAvailability(slug, sessionToken, availability);
            await refreshOwnerData();
            if (advance) setStep(6);
            else setNotice('Availability saved.');
        } catch (err) {
            setError(getErrorMessage(err, 'Could not save your availability.'));
        } finally {
            setSaving(false);
        }
    };

    // --- Identity document handlers ---
    // Carica uno o più file in sequenza; ogni upload restituisce la lista aggiornata (il backend accoda).
    const handleUploadDocuments = async (files: FileList | null) => {
        if (!sessionToken || !files || files.length === 0) return;
        const list = Array.from(files);
        setDocUploading(true);
        setError(null);
        try {
            let latest: IdentityDocument[] | null = null;
            for (const file of list) {
                latest = await uploadIdentityDocument(slug, sessionToken, file);
            }
            if (latest) setIdentityDocuments(latest);
            setNotice(list.length > 1 ? `${list.length} documents uploaded.` : 'Document uploaded.');
        } catch (err) {
            setError(getErrorMessage(err, 'We could not upload one of these files. Please upload PDF or image (PNG or JPEG) files.'));
        } finally {
            setDocUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDeleteDocument = async (index: number) => {
        if (!sessionToken) return;
        setDocUploading(true);
        setError(null);
        try {
            const docs = await deleteIdentityDocument(slug, sessionToken, index);
            setIdentityDocuments(docs);
        } catch (err) {
            setError(getErrorMessage(err, 'Could not remove this document. Please try again.'));
        } finally {
            setDocUploading(false);
        }
    };

    // OTP-mode: request a signing code.
    const handleRequestSignOtp = async () => {
        if (!sessionToken) return;
        setSignOtpSending(true);
        setError(null);
        setNotice(null);
        try {
            const res = await requestSignOtp(slug, sessionToken);
            setSignMaskedEmail(res.maskedEmail);
            setSignOtpRequested(true);
            setNotice(`We sent a signing code to ${res.maskedEmail}.`);
        } catch (err) {
            setError(getErrorMessage(err, 'Could not send the signing code. Please try again.'));
        } finally {
            setSignOtpSending(false);
        }
    };

    const handleSign = async () => {
        if (!sessionToken || !consent) return;
        const mode = ownerData?.signatureMode;

        let payload: SignRequest;
        if (mode === 'OTP') {
            if (signOtpCode.trim().length < 4) {
                setError('Please enter the signing code from your email.');
                return;
            }
            payload = { code: signOtpCode.trim(), consentAccepted: true };
        } else {
            if (!signatureData || !signatureMethod) return;
            payload = {
                signatureImageData: signatureData,
                signatureMethod,
                consentAccepted: true,
            };
        }

        setSaving(true);
        setError(null);
        try {
            const result = await signOnboarding(slug, sessionToken, payload);
            setFinalPdfUrl(result.finalPdfUrl || null);
            setCertVerifyUrl(result.certVerifyUrl || null);
            await refreshOwnerData();
            setStep(7);
        } catch (err) {
            setError(getErrorMessage(err, 'Could not submit your signature. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    const handleSignatureConfirm = (result: { dataUrl: string; method: SignatureMethod }) => {
        setSignatureData(result.dataUrl);
        setSignatureMethod(result.method);
        setShowSignatureModal(false);
    };

    // Auto-dismiss transient notices.
    useEffect(() => {
        if (!notice) return;
        const t = setTimeout(() => setNotice(null), 4000);
        return () => clearTimeout(t);
    }, [notice]);

    const alreadySigned = !!ownerData?.signedAt;
    const allDocsVisited = REVIEW_DOCS.every((d) => visitedTabs.has(d.key));

    const stepper = useMemo(() => {
        if (step < 2 || step > 6) return null;
        return (
            <div className="flex items-center justify-center gap-2 md:gap-4 mb-8">
                {WIZARD_STEPS.map((s, i) => {
                    const active = step === s.key;
                    const done = step > s.key;
                    return (
                        <React.Fragment key={s.key}>
                            <div className="flex items-center gap-2">
                                <div
                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                                        active ? 'bg-brand text-white' : done ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                                    }`}
                                >
                                    {done ? <CheckCircle className="w-4 h-4" /> : i + 1}
                                </div>
                                <span className={`text-sm font-semibold hidden md:block ${active ? 'text-primary' : 'text-gray-400'}`}>
                                    {s.label}
                                </span>
                            </div>
                            {i < WIZARD_STEPS.length - 1 && <div className="w-6 md:w-12 h-0.5 bg-gray-200" />}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    }, [step]);

    // --- Rendering ---
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 animate-spin text-brand" />
            </div>
        );
    }

    if (error && !publicInfo) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
                <div className="max-w-md w-full bg-white rounded-3xl shadow-sm border border-gray-200 p-10 text-center">
                    <AlertTriangle className="w-12 h-12 text-brand mx-auto mb-4" />
                    <h1 className="text-2xl font-serif font-bold text-primary mb-2">Link unavailable</h1>
                    <p className="text-gray-500">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            {/* Header */}
            <header className="bg-white border-b border-gray-200">
                <div className="max-w-4xl mx-auto px-6 py-5 flex items-center gap-3">
                    <img src="/logo1.webp" alt="Another Home" className="w-9 h-9 object-contain" />
                    <div className="flex flex-col leading-none">
                        <span className="font-serif font-bold text-lg text-primary">Another Home</span>
                        <span className="text-[0.6rem] uppercase tracking-widest font-semibold text-gray-400 mt-1">
                            Owner Onboarding
                        </span>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 py-10">
                {/* Global alerts */}
                {error && publicInfo && (
                    <div className="mb-6 bg-red-50 text-brand text-sm p-4 rounded-xl border border-red-100 flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                        {error}
                    </div>
                )}
                {notice && (
                    <div className="mb-6 bg-green-50 text-green-700 text-sm p-4 rounded-xl border border-green-200 flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" />
                        {notice}
                    </div>
                )}

                {stepper}

                {/* STEP 0 — Landing */}
                {step === 0 && publicInfo && (
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-8 md:p-12 text-center animate-fade-in">
                        <div className="w-16 h-16 rounded-2xl bg-brand/10 flex items-center justify-center mx-auto mb-6">
                            <ShieldCheck className="w-8 h-8 text-brand" />
                        </div>
                        <h1 className="text-3xl md:text-4xl font-serif font-bold text-primary mb-3">
                            Welcome, {publicInfo.ownerFirstName}
                        </h1>
                        <p className="text-gray-500 max-w-xl mx-auto mb-2">
                            Let&apos;s get your property ready to host. This secure onboarding takes just a few minutes.
                        </p>
                        <div className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-full px-4 py-2 my-6">
                            <MapPin className="w-4 h-4 text-brand" />
                            {publicInfo.propertyAddress}
                        </div>
                        <div>
                            <button
                                onClick={() => setStep(1)}
                                className="inline-flex items-center gap-2 bg-brand text-white px-8 py-4 rounded-2xl font-bold hover:bg-[#c42e40] shadow-lg transition-all active:scale-[0.98]"
                            >
                                Start <ArrowRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 1 — OTP */}
                {step === 1 && (
                    <div className="max-w-md mx-auto bg-white rounded-3xl shadow-sm border border-gray-200 p-8 md:p-10 animate-fade-in">
                        <div className="w-14 h-14 rounded-2xl bg-brand/10 flex items-center justify-center mx-auto mb-6">
                            <KeyRound className="w-7 h-7 text-brand" />
                        </div>
                        <h2 className="text-2xl font-serif font-bold text-primary mb-2 text-center">Verify your identity</h2>
                        <p className="text-gray-500 text-sm text-center mb-8">
                            {otpRequested
                                ? `Enter the 6-digit code we emailed to ${maskedEmail}.`
                                : `We’ll email a 6-digit code to ${maskedEmail || 'your address on file'}.`}
                        </p>

                        {!otpRequested ? (
                            <button
                                onClick={handleRequestOtp}
                                disabled={otpSending}
                                className="w-full flex items-center justify-center gap-2 bg-brand text-white py-3.5 rounded-2xl font-bold hover:bg-[#c42e40] shadow-md transition-all disabled:opacity-60"
                            >
                                {otpSending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mail className="w-5 h-5" />}
                                Send code
                            </button>
                        ) : (
                            <div className="space-y-5">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    value={otpCode}
                                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                                    placeholder="000000"
                                    className="w-full text-center tracking-[0.5em] text-2xl font-bold px-4 py-4 rounded-2xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                />
                                <button
                                    onClick={handleVerifyOtp}
                                    disabled={saving}
                                    className="w-full flex items-center justify-center gap-2 bg-brand text-white py-3.5 rounded-2xl font-bold hover:bg-[#c42e40] shadow-md transition-all disabled:opacity-60"
                                >
                                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Verify <ArrowRight className="w-5 h-5" /></>}
                                </button>
                                <button
                                    onClick={handleRequestOtp}
                                    disabled={otpSending}
                                    className="w-full text-sm font-semibold text-gray-500 hover:text-brand transition-colors disabled:opacity-60"
                                >
                                    {otpSending ? 'Sending…' : 'Resend code'}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* STEP 2 — Property Setup */}
                {step === 2 && (
                    <div className="animate-fade-in">
                        <div className="mb-6">
                            <h2 className="text-2xl font-serif font-bold text-primary">Property Setup</h2>
                            <p className="text-gray-500 text-sm mt-1">
                                Help us prepare your home. You can save and come back anytime within your session.
                            </p>
                        </div>

                        <PropertySetupForm value={propertyAnchors} onChange={setPropertyAnchors} />
                        <div className="mt-8 flex flex-col sm:flex-row justify-end gap-3">
                            <button
                                onClick={() => handleSavePropertySetup(false)}
                                disabled={saving}
                                className="px-6 py-3 rounded-2xl font-bold border border-gray-300 text-gray-600 hover:bg-gray-100 transition-all disabled:opacity-60"
                            >
                                Save progress
                            </button>
                            <button
                                onClick={() => handleSavePropertySetup(true)}
                                disabled={saving}
                                className="inline-flex items-center justify-center gap-2 bg-brand text-white px-8 py-3 rounded-2xl font-bold hover:bg-[#c42e40] shadow-md transition-all disabled:opacity-60"
                            >
                                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Save &amp; continue <ArrowRight className="w-5 h-5" /></>}
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 3 — Owner Form */}
                {step === 3 && (
                    <div className="animate-fade-in">
                        <div className="mb-6">
                            <h2 className="text-2xl font-serif font-bold text-primary">Owner Onboarding Form</h2>
                            <p className="text-gray-500 text-sm mt-1">
                                Your details for the management agreement and payouts.
                            </p>
                        </div>
                        <OwnerFormFields value={ownerForm} onChange={setOwnerForm} />
                        <div className="mt-8 flex flex-col sm:flex-row justify-between gap-3">
                            <button
                                onClick={() => setStep(2)}
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border border-gray-300 text-gray-600 hover:bg-gray-100 transition-all"
                            >
                                <ArrowLeft className="w-5 h-5" /> Back
                            </button>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={() => handleSaveOwnerForm(false)}
                                    disabled={saving}
                                    className="px-6 py-3 rounded-2xl font-bold border border-gray-300 text-gray-600 hover:bg-gray-100 transition-all disabled:opacity-60"
                                >
                                    Save progress
                                </button>
                                <button
                                    onClick={() => handleSaveOwnerForm(true)}
                                    disabled={saving}
                                    className="inline-flex items-center justify-center gap-2 bg-brand text-white px-8 py-3 rounded-2xl font-bold hover:bg-[#c42e40] shadow-md transition-all disabled:opacity-60"
                                >
                                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Save &amp; continue <ArrowRight className="w-5 h-5" /></>}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* STEP 4 — Identity Documents */}
                {step === 4 && (
                    <div className="animate-fade-in">
                        <div className="mb-6">
                            <h2 className="text-2xl font-serif font-bold text-primary">Identity Documents</h2>
                            <p className="text-gray-500 text-sm mt-1">
                                Upload a government-issued ID (and, if relevant, a bank document). This verifies your
                                identity and its cryptographic hash is embedded in your signed Certificate of Authenticity.
                                At least one document is required before you can sign.
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8">
                            {/* Upload control */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="application/pdf,image/png,image/jpeg"
                                multiple
                                className="hidden"
                                onChange={(e) => handleUploadDocuments(e.target.files)}
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={docUploading}
                                className="w-full inline-flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 text-gray-600 px-6 py-8 rounded-2xl font-bold hover:border-brand hover:text-brand transition-all disabled:opacity-60"
                            >
                                {docUploading ? (
                                    <Loader2 className="w-7 h-7 animate-spin" />
                                ) : (
                                    <UploadCloud className="w-7 h-7" />
                                )}
                                <span>{docUploading ? 'Uploading…' : 'Upload documents'}</span>
                                <span className="text-xs font-medium text-gray-400">PDF, PNG or JPEG — you can select multiple files</span>
                            </button>

                            {/* Uploaded documents list */}
                            {identityDocuments.length > 0 && (
                                <ul className="mt-6 space-y-3">
                                    {identityDocuments.map((doc, i) => (
                                        <li
                                            key={`${doc.sha256}-${i}`}
                                            className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4"
                                        >
                                            <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                                                <IdCard className="w-5 h-5 text-brand" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="font-bold text-sm text-gray-800 truncate">{doc.filename}</p>
                                                <p className="text-xs text-gray-400 font-mono truncate">
                                                    #{doc.sha256.slice(0, 12)}…
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteDocument(i)}
                                                disabled={docUploading}
                                                aria-label={`Remove ${doc.filename}`}
                                                className="p-2 rounded-xl text-gray-400 hover:text-brand hover:bg-red-50 transition-all disabled:opacity-60"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {identityDocuments.length === 0 && (
                                <p className="mt-6 text-sm text-gray-400 text-center">
                                    No documents uploaded yet.
                                </p>
                            )}
                        </div>

                        <div className="mt-8 flex flex-col sm:flex-row justify-between gap-3">
                            <button
                                onClick={() => setStep(3)}
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border border-gray-300 text-gray-600 hover:bg-gray-100 transition-all"
                            >
                                <ArrowLeft className="w-5 h-5" /> Back
                            </button>
                            <button
                                onClick={() => setStep(5)}
                                disabled={identityDocuments.length === 0 || docUploading}
                                className="inline-flex items-center justify-center gap-2 bg-brand text-white px-8 py-3 rounded-2xl font-bold hover:bg-[#c42e40] shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Continue <ArrowRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 5 — Availability (Exhibit A) */}
                {step === 5 && (
                    <div className="animate-fade-in">
                        <div className="mb-6">
                            <h2 className="text-2xl font-serif font-bold text-primary flex items-center gap-2">
                                <CalendarDays className="w-6 h-6 text-brand" /> Availability
                            </h2>
                            <p className="text-gray-500 text-sm mt-1">
                                Set your property&apos;s availability for each month. For every period
                                (Month, 2&nbsp;Weeks, Week, Weekend, Midweek) choose <span className="font-semibold text-green-600">Open</span> or{' '}
                                <span className="font-semibold text-gray-600">Blocked</span>, and set a minimum stay.
                                This fills Exhibit&nbsp;A of your Co-hosting Agreement.
                            </p>
                        </div>

                        <AvailabilityForm value={availability} onChange={setAvailability} />

                        <div className="mt-8 flex flex-col sm:flex-row justify-between gap-3">
                            <button
                                onClick={() => setStep(4)}
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border border-gray-300 text-gray-600 hover:bg-gray-100 transition-all"
                            >
                                <ArrowLeft className="w-5 h-5" /> Back
                            </button>
                            <button
                                onClick={() => handleSaveAvailability(true)}
                                disabled={saving}
                                className="inline-flex items-center justify-center gap-2 bg-brand text-white px-8 py-3 rounded-2xl font-bold hover:bg-[#c42e40] shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continue <ArrowRight className="w-5 h-5" /></>}
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 6 — Review & Sign */}
                {step === 6 && (
                    <div className="animate-fade-in">
                        <div className="mb-6">
                            <h2 className="text-2xl font-serif font-bold text-primary">Review &amp; Sign</h2>
                            <p className="text-gray-500 text-sm mt-1">
                                Review your information, then sign to complete your onboarding.
                            </p>
                        </div>

                        {/* Note di modifica autorizzate: da leggere prima della firma (sono già incluse nei documenti). */}
                        {ownerData?.amendments && (Object.keys(ownerData.amendments) as AmendmentDocKey[])
                            .some((k) => (ownerData.amendments?.[k]?.length ?? 0) > 0) && (
                            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 md:p-6 mb-8">
                                <div className="flex items-center gap-2 mb-3">
                                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                                    <h3 className="font-serif font-bold text-lg text-amber-900">Negotiated modifications</h3>
                                </div>
                                <p className="text-sm text-amber-800 mb-4">
                                    The following changes were authorized for your agreement and are included in the documents below. Please review them before signing.
                                </p>
                                <div className="space-y-4">
                                    {(Object.keys(AMENDMENT_DOC_LABELS) as AmendmentDocKey[])
                                        .filter((k) => (ownerData.amendments?.[k]?.length ?? 0) > 0)
                                        .map((k) => (
                                            <div key={k}>
                                                <p className="font-bold text-sm text-amber-900 mb-1">{AMENDMENT_DOC_LABELS[k]}</p>
                                                <ol className="list-decimal list-inside space-y-1">
                                                    {(ownerData.amendments?.[k] ?? []).map((am, i) => (
                                                        <li key={i} className="text-sm text-amber-800">
                                                            {am.reference ? <span className="font-semibold">{am.reference}: </span> : null}
                                                            {am.text}
                                                        </li>
                                                    ))}
                                                </ol>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                            <div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-center gap-3">
                                <CheckCircle className={`w-6 h-6 ${ownerData?.propertySetupCompleted ? 'text-green-500' : 'text-gray-300'}`} />
                                <div>
                                    <p className="font-bold text-sm text-gray-800">Property setup</p>
                                    <p className="text-xs text-gray-400">
                                        {ownerData?.propertySetupCompleted ? 'Completed' : 'Not completed'}
                                    </p>
                                </div>
                                <button onClick={() => setStep(2)} className="ml-auto text-xs font-bold text-brand hover:underline">
                                    Edit
                                </button>
                            </div>
                            <div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-center gap-3">
                                <CheckCircle className={`w-6 h-6 ${ownerData?.ownerFormCompleted ? 'text-green-500' : 'text-gray-300'}`} />
                                <div>
                                    <p className="font-bold text-sm text-gray-800">Owner form</p>
                                    <p className="text-xs text-gray-400">
                                        {ownerData?.ownerFormCompleted ? 'Completed' : 'Not completed'}
                                    </p>
                                </div>
                                <button onClick={() => setStep(3)} className="ml-auto text-xs font-bold text-brand hover:underline">
                                    Edit
                                </button>
                            </div>
                        </div>

                        {/* Per-document previews shown as tabs. Open each tab to review it. */}
                        <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 mb-8">
                            <h3 className="font-serif font-bold text-lg text-primary mb-4 flex items-center gap-2">
                                <FileText className="w-5 h-5 text-brand" /> Your documents
                            </h3>

                            {/* Tabs */}
                            <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-5">
                                {REVIEW_DOCS.map((d) => {
                                    const active = activeDoc === d.key;
                                    const visited = visitedTabs.has(d.key);
                                    return (
                                        <button
                                            key={d.key}
                                            type="button"
                                            onClick={() => selectDoc(d.key)}
                                            className={`relative -mb-px inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold rounded-t-xl border-b-2 transition-colors ${
                                                active
                                                    ? 'text-brand border-brand'
                                                    : 'text-gray-500 border-transparent hover:text-brand'
                                            }`}
                                        >
                                            {visited && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
                                            {d.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Active document preview */}
                            {docLoading[activeDoc] ? (
                                <div className="flex items-center justify-center gap-2 text-gray-400 py-16 rounded-2xl border border-gray-200 bg-gray-50">
                                    <Loader2 className="w-5 h-5 animate-spin" /> Loading document…
                                </div>
                            ) : docErrors[activeDoc] ? (
                                <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-sm text-brand flex items-start gap-3">
                                    <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                                    <div className="space-y-2">
                                        <span>{docErrors[activeDoc]}</span>
                                        <button
                                            type="button"
                                            onClick={() => loadDoc(activeDoc)}
                                            className="flex items-center gap-1.5 text-sm font-bold text-brand hover:underline"
                                        >
                                            <RefreshCw className="w-3.5 h-3.5" /> Try again
                                        </button>
                                    </div>
                                </div>
                            ) : docUrls[activeDoc] ? (
                                <PdfCanvasViewer
                                    key={activeDoc}
                                    source={docUrls[activeDoc] as string}
                                    title={`${REVIEW_DOCS.find((d) => d.key === activeDoc)?.label} preview`}
                                    maxHeight={560}
                                />
                            ) : null}

                            {/* Review gate */}
                            {!alreadySigned && (
                                <div className="mt-6 pt-6 border-t border-gray-100">
                                    <label
                                        className={`flex items-start gap-3 ${allDocsVisited ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={reviewed}
                                            disabled={!allDocsVisited}
                                            onChange={(e) => setReviewed(e.target.checked)}
                                            className="mt-0.5 h-5 w-5 rounded border-gray-300 text-brand accent-brand focus:ring-brand/30 shrink-0 disabled:opacity-50"
                                        />
                                        <span className="text-sm text-gray-700 font-semibold leading-relaxed">
                                            I have read and reviewed all three documents.
                                        </span>
                                    </label>
                                    {!allDocsVisited && (
                                        <p className="mt-2 ml-8 text-xs text-gray-400">
                                            Open each of the three tabs above to review every document before you can continue.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {alreadySigned ? (
                            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                                <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
                                <p className="font-bold text-gray-800">You have already signed this agreement.</p>
                                <button
                                    onClick={() => setStep(7)}
                                    className="mt-4 inline-flex items-center gap-2 text-brand font-bold hover:underline"
                                >
                                    View confirmation <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        ) : reviewed ? (
                            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
                                <h3 className="font-serif font-bold text-lg text-primary mb-4 flex items-center gap-2">
                                    <PenLine className="w-5 h-5 text-brand" /> Your signature
                                </h3>

                                {ownerData?.signatureMode === 'OTP' ? (
                                    // --- OTP signing flow ---
                                    <div className="space-y-4">
                                        <p className="text-sm text-gray-500">
                                            To sign digitally, request a one-time code. We&apos;ll email it to your address on file.
                                        </p>
                                        {!signOtpRequested ? (
                                            <button
                                                type="button"
                                                onClick={handleRequestSignOtp}
                                                disabled={signOtpSending}
                                                className="inline-flex items-center justify-center gap-2 bg-brand text-white px-6 py-3 rounded-2xl font-bold hover:bg-[#c42e40] shadow-md transition-all disabled:opacity-60"
                                            >
                                                {signOtpSending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mail className="w-5 h-5" />}
                                                Sign digitally
                                            </button>
                                        ) : (
                                            <div className="space-y-3">
                                                <p className="text-sm text-gray-500">
                                                    Enter the signing code we emailed to {signMaskedEmail || 'your address on file'}.
                                                </p>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={6}
                                                    value={signOtpCode}
                                                    onChange={(e) => setSignOtpCode(e.target.value.replace(/\D/g, ''))}
                                                    placeholder="000000"
                                                    className="w-full max-w-xs text-center tracking-[0.5em] text-2xl font-bold px-4 py-4 rounded-2xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleRequestSignOtp}
                                                    disabled={signOtpSending}
                                                    className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-brand transition-colors disabled:opacity-60"
                                                >
                                                    <RefreshCw className="w-3.5 h-3.5" /> {signOtpSending ? 'Sending…' : 'Resend code'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    // --- DRAW_OR_UPLOAD signing flow ---
                                    <div>
                                        {signatureData ? (
                                            <div className="space-y-3">
                                                <div
                                                    className="rounded-2xl border border-gray-200 p-4 flex items-center justify-center"
                                                    style={{
                                                        backgroundImage:
                                                            'linear-gradient(45deg,#f3f4f6 25%,transparent 25%),linear-gradient(-45deg,#f3f4f6 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#f3f4f6 75%),linear-gradient(-45deg,transparent 75%,#f3f4f6 75%)',
                                                        backgroundSize: '16px 16px',
                                                        backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
                                                    }}
                                                >
                                                    <img src={signatureData} alt="Your signature" className="max-h-32 object-contain" />
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                                                        {signatureMethod === 'UPLOADED' ? 'Uploaded signature' : 'Drawn signature'}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowSignatureModal(true)}
                                                        className="text-xs font-bold text-brand hover:underline"
                                                    >
                                                        Change signature
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => setShowSignatureModal(true)}
                                                className="inline-flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 text-gray-600 px-6 py-4 rounded-2xl font-bold hover:border-brand hover:text-brand transition-all w-full sm:w-auto"
                                            >
                                                <PenLine className="w-5 h-5" /> Sign
                                            </button>
                                        )}
                                    </div>
                                )}

                                <label className="flex items-start gap-3 mt-6 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={consent}
                                        onChange={(e) => setConsent(e.target.checked)}
                                        className="mt-0.5 h-5 w-5 rounded border-gray-300 text-brand accent-brand focus:ring-brand/30 shrink-0"
                                    />
                                    <span className="text-sm text-gray-600 leading-relaxed">
                                        I consent to sign electronically under the ESIGN Act / ESRA, and I agree that my
                                        electronic signature is the legal equivalent of my handwritten signature on the
                                        Another Home management agreement.
                                    </span>
                                </label>

                                <div className="mt-8 flex flex-col sm:flex-row justify-between gap-3">
                                    <button
                                        onClick={() => setStep(5)}
                                        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border border-gray-300 text-gray-600 hover:bg-gray-100 transition-all"
                                    >
                                        <ArrowLeft className="w-5 h-5" /> Back
                                    </button>
                                    <button
                                        onClick={handleSign}
                                        disabled={
                                            saving ||
                                            !consent ||
                                            (ownerData?.signatureMode === 'OTP'
                                                ? signOtpCode.trim().length < 4
                                                : !signatureData)
                                        }
                                        className="inline-flex items-center justify-center gap-2 bg-brand text-white px-8 py-3 rounded-2xl font-bold hover:bg-[#c42e40] shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Confirm &amp; Sign <ShieldCheck className="w-5 h-5" /></>}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-start">
                                <button
                                    onClick={() => setStep(5)}
                                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border border-gray-300 text-gray-600 hover:bg-gray-100 transition-all"
                                >
                                    <ArrowLeft className="w-5 h-5" /> Back
                                </button>
                            </div>
                        )}

                        {showSignatureModal && (
                            <SignatureModal
                                onClose={() => setShowSignatureModal(false)}
                                onConfirm={handleSignatureConfirm}
                            />
                        )}
                    </div>
                )}

                {/* STEP 7 — Confirmation */}
                {step === 7 && (
                    <div className="max-w-lg mx-auto bg-white rounded-3xl shadow-sm border border-gray-200 p-8 md:p-12 text-center animate-fade-in">
                        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="w-9 h-9 text-green-600" />
                        </div>
                        <h1 className="text-3xl font-serif font-bold text-primary mb-3">You&apos;re all set!</h1>
                        <p className="text-gray-500 mb-8">
                            Thank you for completing your onboarding. Your signed agreement is being certified and a copy
                            has been sent to your email.
                        </p>

                        <div className="space-y-3">
                            {documents.length > 0 ? (
                                documents.map((doc) => (
                                    <a
                                        key={doc.label}
                                        href={doc.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white py-3.5 rounded-2xl font-bold hover:bg-[#c42e40] shadow-md transition-all"
                                    >
                                        <Download className="w-5 h-5" /> Download {doc.label}
                                    </a>
                                ))
                            ) : (
                                <div className="w-full inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-500 py-3.5 rounded-2xl font-bold">
                                    <Loader2 className="w-5 h-5 animate-spin" /> Preparing your documents…
                                </div>
                            )}

                            {certificateUrl && (
                                <a
                                    href={certificateUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="w-full inline-flex items-center justify-center gap-2 bg-green-600 text-white py-3.5 rounded-2xl font-bold hover:bg-green-700 shadow-md transition-all"
                                >
                                    <BadgeCheck className="w-5 h-5" /> Download your Certificate of Authenticity
                                </a>
                            )}

                            {certVerifyUrl && (
                                <a
                                    href={certVerifyUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="w-full inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-700 py-3.5 rounded-2xl font-bold hover:bg-gray-50 transition-all"
                                >
                                    <ExternalLink className="w-5 h-5" /> Verify certification
                                </a>
                            )}
                        </div>

                        {ownerData?.certTxId && (
                            <div className="mt-6 text-xs text-gray-400 flex items-start justify-center gap-1.5">
                                <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                <span className="break-all min-w-0">Certificate ID: {ownerData.certTxId}</span>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};

export default OwnerOnboarding;
